import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api-client";
import { apiEndpoints } from "@/lib/api";
import type { Notification } from "@/features/notifications/types/notifications";

// ─── Portal projects (client-scoped, safe DTOs) ─────────────────────────────

export const usePortalProjects = () => {
  return useQuery({
    queryKey: ["portal", "projects"],
    queryFn: () => api.get(apiEndpoints.portal.projects.getAll),
  });
};

export const usePortalProject = (projectId: string | undefined) => {
  return useQuery({
    queryKey: ["portal", "project", projectId],
    queryFn: () => api.get(apiEndpoints.portal.projects.getOneById(projectId!)),
    enabled: !!projectId,
  });
};

export const usePortalProjectOverview = (projectId: string | undefined) => {
  return useQuery({
    queryKey: ["portal", "project-overview", projectId],
    queryFn: () =>
      api.get(apiEndpoints.portal.projects.getOverview(projectId!)),
    enabled: !!projectId,
    throwOnError: true,
  });
};

// ─── Portal milestones (client-scoped, safe DTOs) ───────────────────────────

export const usePortalMilestones = (projectId: string | undefined) => {
  return useQuery({
    queryKey: ["portal", "milestones", projectId],
    queryFn: () =>
      api.get(apiEndpoints.portal.milestones.getAll(projectId!)),
    enabled: !!projectId,
    throwOnError: true,
  });
};

export const usePortalMilestone = (
  projectId: string | undefined,
  milestoneId: string | undefined
) => {
  return useQuery({
    queryKey: ["portal", "milestone", projectId, milestoneId],
    queryFn: () =>
      api.get(
        apiEndpoints.portal.milestones.getOneById(
          projectId!,
          milestoneId!
        )
      ),
    enabled: !!projectId && !!milestoneId,
    throwOnError: true,
  });
};

// ─── Portal daily logs (client-scoped, safe DTOs) ───────────────────────────

export const usePortalDailyLogs = (projectId: string | undefined) => {
  return useQuery({
    queryKey: ["portal", "daily-logs", projectId],
    queryFn: () =>
      api.get(apiEndpoints.portal.dailyLogs.getAll(projectId!)),
    enabled: !!projectId,
    throwOnError: true,
  });
};

export const usePortalDailyLog = (
  projectId: string | undefined,
  logId: string | undefined
) => {
  return useQuery({
    queryKey: ["portal", "daily-log", projectId, logId],
    queryFn: () =>
      api.get(
        apiEndpoints.portal.dailyLogs.getOneById(projectId!, logId!)
      ),
    enabled: !!projectId && !!logId,
    throwOnError: true,
  });
};

// ─── Portal notifications (client user scoped) ──────────────────────────────

export const usePortalNotifications = () => {
  return useQuery({
    queryKey: ["portal", "notifications"],
    queryFn: () => api.get(apiEndpoints.portal.notifications.getAll),
    select: (res) => res.data as Notification[],
    refetchInterval: 60_000,
    staleTime: 30_000,
  });
};

export const usePortalUnreadCount = () => {
  return useQuery({
    queryKey: ["portal", "notifications", "unread-count"],
    queryFn: () => api.get(apiEndpoints.portal.notifications.unreadCount),
    select: (res) => res.data.unreadCount as number,
    staleTime: 30_000,
  });
};

export const usePortalMarkNotificationRead = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.patch(apiEndpoints.portal.notifications.markRead(id)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["portal", "notifications"] });
    },
  });
};

export const usePortalMarkAllNotificationsRead = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (projectId?: string) =>
      api.patch(apiEndpoints.portal.notifications.markAllRead, { projectId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["portal", "notifications"] });
    },
  });
};

// ─── Portal material decisions (client-scoped, safe DTOs) ──────────────────

export const usePortalMaterialDecisions = (projectId: string | undefined) => {
  return useQuery({
    queryKey: ["portal", "material-decisions", projectId],
    queryFn: () =>
      api.get(apiEndpoints.portal.materialDecisions.getAll(projectId!)),
    enabled: !!projectId,
    throwOnError: true,
  });
};

export const usePortalMaterialDecision = (
  projectId: string | undefined,
  decisionId: string | undefined
) => {
  return useQuery({
    queryKey: ["portal", "material-decision", projectId, decisionId],
    queryFn: () =>
      api.get(
        apiEndpoints.portal.materialDecisions.getOneById(
          projectId!,
          decisionId!
        )
      ),
    enabled: !!projectId && !!decisionId,
    throwOnError: true,
  });
};

export const usePortalMaterialPendingCount = (
  projectId: string | undefined
) => {
  return useQuery({
    queryKey: ["portal", "material-decisions", "pending-count", projectId],
    queryFn: () =>
      api.get(apiEndpoints.portal.materialDecisions.pendingCount(projectId!)),
    enabled: !!projectId,
    throwOnError: true,
  });
};

// ─── Portal change orders ─────────────────────────────────────────────────────

export const usePortalChangeOrders = (projectId: string | undefined) => {
  return useQuery({
    queryKey: ["portal", "change-orders", projectId],
    queryFn: () =>
      api.get(apiEndpoints.portal.changeOrders.getAll(projectId!)),
    enabled: !!projectId,
    throwOnError: true,
  });
};

export const usePortalChangeOrder = (
  projectId: string | undefined,
  changeOrderId: string | undefined
) => {
  return useQuery({
    queryKey: ["portal", "change-order", projectId, changeOrderId],
    queryFn: () =>
      api.get(
        apiEndpoints.portal.changeOrders.getOneById(
          projectId!,
          changeOrderId!
        )
      ),
    enabled: !!projectId && !!changeOrderId,
    throwOnError: true,
  });
};

const getPortalChangeOrderPdfUrl = async (
  projectId: string,
  changeOrderId: string
): Promise<{ url: string }> => {
  const res: any = await api.get(
    apiEndpoints.portal.changeOrders.getPdfUrl(projectId, changeOrderId)
  );
  return res.data;
};

export const usePortalChangeOrderPdfUrl = (
  projectId: string | undefined,
  changeOrderId: string | undefined
) => {
  return useQuery<{ url: string }>({
    queryKey: ["portal", "change-order", projectId, changeOrderId, "pdf"],
    queryFn: () =>
      getPortalChangeOrderPdfUrl(projectId!, changeOrderId!),
    enabled: !!projectId && !!changeOrderId,
  });
};

export const usePortalPendingChangeOrderCount = (
  projectId: string | undefined
) => {
  return useQuery({
    queryKey: ["portal", "change-orders", "pending-count", projectId],
    queryFn: async () => {
      const res: any = await api.get(
        apiEndpoints.portal.changeOrders.getAll(projectId!)
      );
      const all = res.data ?? [];
      return all.filter((co: any) => co.status === "pending_client").length;
    },
    enabled: !!projectId,
    throwOnError: true,
  });
};

export const usePortalApproveChangeOrder = (projectId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      changeOrderId,
      signaturePngBase64,
    }: {
      changeOrderId: string;
      signaturePngBase64: string;
    }) =>
      api.post(
        apiEndpoints.portal.changeOrders.approve(projectId, changeOrderId),
        { signature_png_base64: signaturePngBase64 }
      ),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["portal", "change-orders", projectId],
      });
      queryClient.invalidateQueries({
        queryKey: ["portal", "change-order", projectId, variables.changeOrderId],
      });
      queryClient.invalidateQueries({
        queryKey: ["portal", "project-overview", projectId],
      });
    },
  });
};

export const usePortalRejectChangeOrder = (projectId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      changeOrderId,
      reason,
    }: {
      changeOrderId: string;
      reason: string;
    }) =>
      api.post(
        apiEndpoints.portal.changeOrders.reject(projectId, changeOrderId),
        { reason }
      ),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["portal", "change-orders", projectId],
      });
      queryClient.invalidateQueries({
        queryKey: ["portal", "change-order", projectId, variables.changeOrderId],
      });
      queryClient.invalidateQueries({
        queryKey: ["portal", "project-overview", projectId],
      });
    },
  });
};

export const usePortalSetSourcingPreference = (projectId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      decisionId,
      preference,
      deliveryDate,
    }: {
      decisionId: string;
      preference: "self_supply" | "request_sourcing";
      deliveryDate?: string;
    }) =>
      api.post(
        apiEndpoints.portal.materialDecisions.setSourcingPreference(
          projectId,
          decisionId
        ),
        { preference, delivery_date: deliveryDate }
      ),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["portal", "material-decisions", projectId],
      });
      queryClient.invalidateQueries({
        queryKey: ["portal", "material-decision", projectId, variables.decisionId],
      });
      queryClient.invalidateQueries({
        queryKey: ["portal", "material-decisions", "pending-count", projectId],
      });
      queryClient.invalidateQueries({
        queryKey: ["portal", "project-overview", projectId],
      });
    },
  });
};

export const usePortalSelectOption = (projectId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      decisionId,
      optionId,
    }: {
      decisionId: string;
      optionId: string;
    }) =>
      api.post(
        apiEndpoints.portal.materialDecisions.selectOption(projectId, decisionId),
        { option_id: optionId }
      ),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["portal", "material-decisions", projectId],
      });
      queryClient.invalidateQueries({
        queryKey: ["portal", "material-decision", projectId, variables.decisionId],
      });
      queryClient.invalidateQueries({
        queryKey: ["portal", "material-decisions", "pending-count", projectId],
      });
      queryClient.invalidateQueries({
        queryKey: ["portal", "project-overview", projectId],
      });
    },
  });
};

// ─── Portal client invoices ──────────────────────────────────────────────────

export const usePortalClientInvoices = (projectId: string | undefined) => {
  return useQuery({
    queryKey: ["portal", "client-invoices", projectId],
    queryFn: () =>
      api.get(apiEndpoints.portal.clientInvoices.getAll(projectId!)),
    enabled: !!projectId,
    throwOnError: true,
  });
};

export const usePortalClientInvoice = (
  projectId: string | undefined,
  invoiceId: string | undefined
) => {
  return useQuery({
    queryKey: ["portal", "client-invoice", projectId, invoiceId],
    queryFn: () =>
      api.get(
        apiEndpoints.portal.clientInvoices.getOneById(
          projectId!,
          invoiceId!
        )
      ),
    enabled: !!projectId && !!invoiceId,
    throwOnError: true,
  });
};

export const usePortalAllClientInvoices = () => {
  return useQuery({
    queryKey: ["portal", "all-client-invoices"],
    queryFn: () => api.get(apiEndpoints.portal.clientInvoices.getAllForClient),
    throwOnError: true,
  });
};

export const usePortalClientInvoiceDetail = (invoiceId: string | undefined) => {
  return useQuery({
    queryKey: ["portal", "client-invoice-detail", invoiceId],
    queryFn: () =>
      api.get(apiEndpoints.portal.clientInvoices.getDetail(invoiceId!)),
    enabled: !!invoiceId,
    throwOnError: true,
  });
};

export const usePortalReportPayment = (projectId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      invoiceId,
      amount_reported,
      proof,
      client_notes,
    }: {
      invoiceId: string;
      amount_reported: number;
      proof?: File;
      client_notes?: string;
    }) => {
      const formData = new FormData();
      formData.append("amount_reported", amount_reported.toString());
      if (proof) {
        formData.append("proof", proof);
      }
      if (client_notes) {
        formData.append("client_notes", client_notes);
      }
      return api.post(
        apiEndpoints.portal.clientInvoices.reportPayment(projectId, invoiceId),
        formData
      );
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["portal", "client-invoices", projectId],
      });
      queryClient.invalidateQueries({
        queryKey: ["portal", "client-invoice", projectId, variables.invoiceId],
      });
      queryClient.invalidateQueries({
        queryKey: ["portal", "client-invoice-detail", variables.invoiceId],
      });
      queryClient.invalidateQueries({
        queryKey: ["portal", "project-overview", projectId],
      });
    },
  });
};

// ─── Portal punchlists (client "Report Defect") ───────────────────────────────

export const usePortalPunchlists = (projectId: string | undefined) => {
  return useQuery({
    queryKey: ["portal-punchlists", projectId],
    queryFn: () =>
      api.get(apiEndpoints.punchlists.portalGetAll(projectId!)),
    enabled: !!projectId,
    throwOnError: true,
  });
};

export const usePortalCreatePunchlist = (projectId: string | undefined) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: {
      description: string;
      location: string;
      priority?: string;
      photos?: File[];
    }) => {
      const formData = new FormData();
      formData.append("description", data.description);
      formData.append("location", data.location);
      if (data.priority) formData.append("priority", data.priority);
      (data.photos ?? []).forEach((photo) =>
        formData.append("before_photo", photo)
      );
      return api.post(
        apiEndpoints.punchlists.portalCreate(projectId!),
        formData
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["portal-punchlists", projectId],
      });
    },
  });
};

export const usePortalPunchlistDetail = (projectId: string | undefined, itemId: string | undefined) => {
  return useQuery({
    queryKey: ["portal-punchlist", projectId, itemId],
    queryFn: async () => {
      const res: any = await api.get(apiEndpoints.punchlists.portalGetOneById(projectId!, itemId!));
      return res.data;
    },
    enabled: !!projectId && !!itemId,
  });
};
